import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { FirmaService, FirmaEnvelope, FirmaTemplate, FirmaStatus, FirmaEvent } from 'src/shared/services/firma.service';

interface DetailRow {
  label: string;
  value: string;
}

@Component({
  templateUrl: 'e-sign-detail.html',
  styleUrls: ['./e-sign-detail.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ESignDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private modalController = inject(ModalController);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private firma = inject(FirmaService);
  private featureGate = inject(FeatureGateService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  kind: 'envelope' | 'template';
  envelope: FirmaEnvelope;
  template: FirmaTemplate;
  loaded = false;

  // Precomputed view state — no function calls in the template.
  // Envelope names read "Lease Agreement — Oakwood Apartments Apt 3B". The two
  // halves are split so the header carries the document type and the card below
  // carries the address, instead of both repeating the whole string.
  pageTitle = '';
  cardTitle = '';
  statusText = '';
  statusClass = '';
  timeline: FirmaEvent[] = [];
  detailRows: DetailRow[] = [];
  hasRecipient = false;
  agreementLink = '';
  agreementLinkTitle = '';
  agreementLinkHint = '';
  isSigned = false;
  canSend = false;
  templateUsageLabel = '';
  topNavIcon = 'copy';

  downloadAction = () => this.downloadSigned();
  sendAction = () => this.sendForSignature();
  useAction = () => this.useTemplate();
  editAction = () => this.editTemplate();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.kind = this.navCtrl.getParam('kind') || params?.kind;
    this.envelope = this.navCtrl.getParam('envelope') || params?.envelope;
    this.template = this.navCtrl.getParam('template') || params?.template;

    if (this.envelope) { this.buildEnvelopeView(); }
    if (this.template) { this.buildTemplateView(); }

    if (this.kind === 'template') {
      this.pageTitle = this.template?.name || '';
    }
    if (this.isRightSideContent && this.pageTitle) {
      this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    }
    this.publishHeaderIcons();

    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  // The page's utility action goes in the header — Remove for a template, Copy link
  // for a document — so the body only carries the action that moves things forward.
  private publishHeaderIcons(): void {
    const isTemplate = this.kind === 'template';
    this.topNavIcon = isTemplate ? 'trash' : 'copy';
    if (!this.isRightSideContent) {
      return;
    }
    this.rightSidePanelService.setHeaderIcons([
      isTemplate
        ? { id: 'remove-template', icon: 'assets/svg/trash-outline.svg', tooltip: 'Remove template' }
        : { id: 'copy-link', icon: 'assets/svg/copy-outline.svg', tooltip: 'Copy agreement link' }
    ]);
    this.rightSidePanelService.headerIconClicked$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(iconId => {
      if (iconId === 'remove-template') { this.removeTemplate(); }
      if (iconId === 'copy-link') { this.copyAgreementLink(); }
    });
    this.destroyRef.onDestroy(() => this.rightSidePanelService.clearHeaderIcons());
  }

  /** Mobile top-nav icon — same action the dialog header icon runs on desktop. */
  topNavAction(): void {
    if (this.kind === 'template') {
      this.removeTemplate();
    } else {
      this.copyAgreementLink();
    }
  }

  private buildEnvelopeView(): void {
    const status = this.envelope.status;
    this.statusText = this.statusLabel(status);
    this.statusClass = this.statusModifier(status);
    this.isSigned = status === 'finished';
    this.canSend = status === 'not_sent';
    this.hasRecipient = !!this.envelope.recipient_name;
    this.timeline = this.envelope.timeline || [];

    // "Lease Agreement — Oakwood Apartments Apt 3B" → header gets the type, the
    // card gets the address. Falls back to the whole name for envelopes created
    // through send-envelope, which have no em dash and no property attached.
    const [type, ...address] = this.envelope.name.split('—');
    this.pageTitle = type.trim() || this.envelope.name;
    this.cardTitle =
      [this.envelope.property_name, this.envelope.unit].filter(Boolean).join(' · ')
      || address.join('—').trim()
      || this.pageTitle;

    this.detailRows = [
      { label: 'Template', value: this.envelope.template_name },
      { label: 'Created', value: this.formatDate(this.envelope.created_at) },
      { label: 'Sent', value: this.formatDate(this.envelope.sent_at) },
      { label: this.completedLabel(status), value: this.formatDate(this.envelope.completed_at) },
    ].filter(row => !!row.value);

    this.agreementLink = this.firma.agreementLink(this.envelope);
    this.agreementLinkTitle = this.isSigned ? 'Signed document' : 'Agreement link';
    this.agreementLinkHint = this.isSigned
      ? 'The signed document — share or save this link.'
      : 'A link to this agreement. Share it with anyone who needs to review or sign.';
  }

  private buildTemplateView(): void {
    // Category is omitted — the tag beside the template name already shows it.
    this.detailRows = [
      { label: 'Signers', value: this.template.signers === 1 ? '1 signer' : `${this.template.signers} signers` },
      { label: 'Fields to fill', value: `${this.template.fields}` },
      { label: 'Last updated', value: this.formatDate(this.template.updated_at) },
      { label: 'Last used', value: this.formatDate(this.template.last_used_at) },
    ].filter(row => !!row.value);

    const times = this.template.times_used;
    this.templateUsageLabel = times === 0
      ? 'Not used yet'
      : `Used ${times === 1 ? 'once' : `${times} times`}`;
  }

  private completedLabel(status: FirmaStatus): string {
    switch (status) {
      case 'finished': return 'Signed';
      case 'declined': return 'Declined';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return 'Completed';
    }
  }

  private formatDate(date: string | undefined): string {
    if (!date) { return ''; }
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  statusLabel(status: FirmaStatus): string {
    switch (status) {
      case 'not_sent': return 'Draft';
      case 'in_progress': return 'Awaiting signature';
      case 'finished': return 'Signed';
      case 'declined': return 'Declined';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  private statusModifier(status: FirmaStatus): string {
    switch (status) {
      case 'finished': return 'es-status--signed';
      case 'in_progress': return 'es-status--waiting';
      case 'declined':
      case 'expired': return 'es-status--problem';
      default: return 'es-status--draft';
    }
  }

  copyAgreementLink() {
    try { navigator?.clipboard?.writeText(this.agreementLink); } catch { /* clipboard unavailable */ }
    this.util.showSuccess(`Link copied: ${this.agreementLink}`);
  }

  downloadSigned() {
    // The Firma integration isn't live, so there is no real signed PDF to open yet.
    this.featureGate.gate();
  }

  // Sending and template editing both need the Firma integration, which isn't live
  // for any account yet — these hand off to the account manager instead.
  sendForSignature() {
    this.featureGate.gate();
  }

  useTemplate() {
    this.featureGate.gate();
  }

  editTemplate() {
    this.featureGate.gate();
  }

  async removeTemplate() {
    if (this.featureGate.gate()) { return; }
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Remove Template?',
      body: `"${this.template.name}" will no longer be available to send.`,
      backText: 'Go Back',
      confirmText: 'Remove',
      confirmAction: this.confirmRemoveTemplate.bind(this)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async confirmRemoveTemplate() {
    try {
      await this.firma.removeTemplate(this.template.id);
      this.util.showSuccess(`Removed "${this.template.name}".`);
      this.closePanel();
    } finally {
      this.modalController.dismiss();
    }
  }

  private closePanel() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('e-sign');
    }
  }
}
